import { useMemo } from 'react';
import { useGetConversationByIdQuery } from 'src/store/services';
import { useAppSelector, sessionState } from 'src/store';
import {
  useComparingConversationsParams,
  useConversationParams,
} from 'src/hooks';

/**
 * This hook should be used only for base information about the current conversation.
 * For messages and tasks use separate hooks!
 */
export const useCurrentConversation = (conversationId?: string | null) => {
  const { isCompareMode } = useComparingConversationsParams();
  const {
    appUser: { user_id },
  } = useAppSelector(sessionState);

  const {
    currentConversationId,
    isLandingPage,
    shouldSkipGetConversationById,
  } = useConversationParams(conversationId);

  const {
    data: conversationData,
    isLoading: isConversationLoading,
    isFetching: isConversationFetching,
    isError: isConversationError,
    error: conversationError,
    isSuccess: isConversationSuccess,
  } = useGetConversationByIdQuery(
    {
      user_id,
      conversation_id: conversationId || currentConversationId,
    },
    {
      skip: shouldSkipGetConversationById,
    },
  );

  const currentConversation = useMemo(
    () => (isCompareMode || !isLandingPage ? conversationData : undefined),
    [conversationData, isCompareMode, isLandingPage],
  );

  return useMemo(
    () => ({
      currentConversation,
      isCurrentConversationLoading:
        isConversationLoading || isConversationFetching,
      isConversationSuccess,
      isConversationError,
      conversationError,
    }),
    [
      currentConversation,
      isConversationLoading,
      isConversationFetching,
      isConversationSuccess,
      isConversationError,
      conversationError,
    ],
  );
};
